import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Animated,
  Easing,
  Modal,
} from "react-native";
import {
  ListRowSkeleton,
  MoonFilterChip,
  StatusBadge,
  Tag,
  ClinicalDataStatus,
  MoonSearchInput,
} from "@louise/moon-ui";
import {
  ChevronLeft,
  Plus,
  X,
  FileText,
  Calendar,
  AlertCircle,
  Microscope,
  ScanLine,
  ClipboardList,
  Dna,
  File,
  Check,
  Pill,
  Receipt,
  PenTool,
  ShieldCheck,
  Layers,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { EMBER } from "../../onboarding/theme";
import {
  useDocuments,
  useUpdateDocument,
  type Document,
} from "@src/packages/documents/hooks/useDocuments";
import { useTestResults } from "@src/packages/test-results/hooks/useTestResults";
import type { TestResult } from "@src/packages/test-results/hooks/useTestResults";
import type {
  TreatmentIntent,
  PartnerChoice,
  Condition,
} from "../../onboarding/types";
import type { CareStackParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import {
  useTranslations,
  useLocale,
  type TranslationFunction,
} from "@src/packages/i18n";
import DocumentUploadModal, {
  DocumentFileDropZone,
  DOCUMENT_UPLOAD_CATEGORIES,
  type DocumentSlotInfo,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentUploadModal";
import DocumentUploadInteractionPrototype, {
  DOCUMENT_UPLOAD_PROTOTYPE_STATES,
  type DocumentUploadPrototypeState,
} from "@src/packages/document-vault/DocumentUploadInteractionPrototype";
import DocumentPreviewModal from "./DocumentPreviewModal";
import type { PreviewDocumentInfo } from "./DocumentPreviewModal";
import {
  DocumentRow,
  DocumentTestResultRow,
  type DocumentRowStatus,
} from "./documents/DocumentRow";
import type { ResultInterpretation } from "@src/packages/homepage/plan/intelligence/useTestResultInsights";
import { getDevClinicalDataState } from "@src/packages/care/dev-clinical-data-state";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import { filterDocumentSlotsForExperience } from "./documentsExperience";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ── Types ──

type DocumentStatus = "not_started" | "have_it" | "requested";

type DocumentCategory =
  "medical_records" | "coverage_benefits" | "legal_consent" | "admin";

type DocumentFilterCategory = DocumentCategory | "uncategorized";

/** A clinical value extracted from a parsed document (e.g. AMH: 3.2 ng/mL) */
type ClinicalValue = {
  label: string; // e.g. "AMH", "FSH", "TSH"
  value: string; // e.g. "3.2", "6.8", "Normal"
  unit?: string; // e.g. "ng/mL", "mIU/mL"
  referenceRange?: string; // e.g. "1.0–3.5"
  flag?: "normal" | "low" | "high" | "critical";
};

/** A medication extracted from an OCR-parsed treatment plan */
type MedicationInfo = {
  name: string;
  detail?: string; // e.g. "150 IU"
  frequency?: string;
  startDate?: string;
  timeOfDay?: string;
};

/** Extract medications from a document's med: tags (format: med:name|detail|frequency|startDate|timeOfDay) */
function getMedicationsFromTags(tags: string[]): MedicationInfo[] {
  return tags
    .filter((t) => t.startsWith("med:"))
    .map((t) => {
      const value = t.slice(4);
      const parts = value.split("|");
      return {
        name: parts[0] ?? value,
        detail: parts[1] || undefined,
        frequency: parts[2] || undefined,
        startDate: parts[3] || undefined,
        timeOfDay: parts[4] || undefined,
      };
    });
}

/** An appointment extracted from an OCR-parsed treatment plan */
type AppointmentInfo = {
  title: string;
  type?: string;
  date?: string;
};

/** Extract appointments from a document's appt: tags */
function getAppointmentsFromTags(tags: string[]): AppointmentInfo[] {
  return tags
    .filter((t) => t.startsWith("appt:"))
    .map((t) => {
      const parts = t.slice(5).split("|");
      return {
        title: parts[0] ?? "",
        type: parts[1] || undefined,
        date: parts[2] || undefined,
      };
    });
}

type DocumentSlot = {
  id: string;
  nameKey: string;
  whyKey: string;
  category: DocumentCategory;
  treatmentIntents: TreatmentIntent[] | null;
  partnerChoices: PartnerChoice[] | null;
  conditions: Condition[] | null;
  priority: "essential" | "recommended" | "optional";
  /** When set, this document is tied to a specific treatment cycle */
  cycleId?: string | null;
  /** The date the record/test was actually performed (not upload date) */
  recordDate?: string | null;
  /** Clinical values extracted from the uploaded document */
  clinicalValues?: ClinicalValue[] | null;
};

type DocumentType = "lab" | "imaging" | "treatment_plan" | "genetic" | "other";

/** Maps each document slot to its document type for the tag display */
const SLOT_DOCUMENT_TYPE: Record<string, DocumentType> = {
  // Labs
  amh: "lab",
  "day3-panel": "lab",
  "thyroid-panel": "lab",
  "pcos-labs": "lab",
  "semen-analysis": "lab",
  // Imaging
  "afc-ultrasound": "imaging",
  "hsg-saline": "imaging",
  "endo-imaging": "imaging",
  // Genetic
  "carrier-screening": "genetic",
  "pgt-results": "genetic",
  "genetic-counseling": "genetic",
  // Treatment plan
  "treatment-plan": "treatment_plan",
};

const DOCUMENT_TYPE_META: Record<
  DocumentType,
  { labelKey: string; color: string; icon: typeof FileText }
> = {
  lab: {
    labelKey: "documents.doc_type.lab",
    color: "#6366F1",
    icon: Microscope,
  },
  imaging: {
    labelKey: "documents.doc_type.imaging",
    color: "#0EA5E9",
    icon: ScanLine,
  },
  treatment_plan: {
    labelKey: "documents.doc_type.treatment_plan",
    color: "#F59E0B",
    icon: ClipboardList,
  },
  genetic: {
    labelKey: "documents.doc_type.genetic",
    color: "#A855F7",
    icon: Dna,
  },
  other: { labelKey: "documents.doc_type.other", color: "#78716C", icon: File },
};

const DOCTYPE_TAG_PREFIX = "doctype:";
const DOCUMENT_CYCLE_TAG_PREFIX = "cycle:";
const DOCUMENT_CURRENT_CYCLE_TAG = "cycle:current";
const DOCUMENT_ACTION_NEEDED_FILTER_ID = "action-needed";
const DOCUMENT_UPLOADED_BY_USER_TAG = "uploaded-by:user";

/** Extract a persisted document type from a document's tags array */
function getDocTypeFromTags(tags: string[]): DocumentType | undefined {
  const tag = tags.find((t) => t.startsWith(DOCTYPE_TAG_PREFIX));
  if (!tag) return undefined;
  const value = tag.slice(DOCTYPE_TAG_PREFIX.length);
  if (value in DOCUMENT_TYPE_META) return value as DocumentType;
  return undefined;
}

/** Return a new tags array with the doctype tag set */
function setDocTypeInTags(tags: string[], docType: DocumentType): string[] {
  const filtered = tags.filter((t) => !t.startsWith(DOCTYPE_TAG_PREFIX));
  return [...filtered, `${DOCTYPE_TAG_PREFIX}${docType}`];
}

type DocumentFilterOption = {
  id: string;
  label: string;
  icon: LucideIcon;
};

function getCycleTagFromDocument(doc: { tags: string[] }) {
  return doc.tags.find((tag) => tag.startsWith(DOCUMENT_CYCLE_TAG_PREFIX));
}

function getCycleTypeLabel(type: string, t: TranslationFunction) {
  switch (type) {
    case "ivf":
      return t("documents.cycle_type.ivf");
    case "fet":
      return t("documents.cycle_type.fet");
    case "egg-freezing":
    case "egg_freezing":
      return t("documents.cycle_type.egg_freezing");
    case "embryo-freezing":
    case "embryo_freezing":
      return t("documents.cycle_type.embryo_freezing");
    case "sperm-freezing":
    case "sperm_freezing":
      return t("documents.cycle_type.sperm_freezing");
    case "iui":
      return t("documents.cycle_type.iui");
    default:
      return type
        .split("-")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
  }
}

function parseCycleTag(tag: string) {
  const raw = tag.slice(DOCUMENT_CYCLE_TAG_PREFIX.length);
  const match = raw.match(/^(.+)-(\d+)$/);
  if (!match) return { type: raw, index: null };
  return { type: match[1]!, index: Number(match[2]) };
}

function formatCycleFilterLabel(
  tag: string,
  countByType: Record<string, number>,
  t: TranslationFunction,
) {
  const parsed = parseCycleTag(tag);
  const base = getCycleTypeLabel(parsed.type, t);
  if (parsed.type === "ivf" || parsed.type === "fet" || parsed.type === "iui") {
    return parsed.index ? `${base}${parsed.index}` : base;
  }
  if (parsed.index && (countByType[parsed.type] ?? 0) > 1) {
    return `${base} ${parsed.index}`;
  }
  return base;
}

function isDocumentRequested(doc: {
  uploads: { uploadStatus: "pending" | "uploaded" | null }[];
}) {
  return doc.uploads.some((upload) => upload.uploadStatus === "pending");
}

function buildDocumentFilterOptions(
  documents: Document[] | undefined,
  t: TranslationFunction,
): DocumentFilterOption[] {
  const options: DocumentFilterOption[] = [
    { id: "all", label: t("documents.filter.all"), icon: Layers },
  ];
  if (!documents || documents.length === 0) return options;

  if (documents.some(isDocumentRequested)) {
    options.push({
      id: DOCUMENT_ACTION_NEEDED_FILTER_ID,
      label: t("documents.filter.action_needed"),
      icon: AlertCircle,
    });
  }

  if (documents.some((doc) => doc.tags.includes(DOCUMENT_CURRENT_CYCLE_TAG))) {
    options.push({
      id: DOCUMENT_CURRENT_CYCLE_TAG,
      label: t("documents.filter.current_cycle"),
      icon: Calendar,
    });
  }

  const cycleTags = Array.from(
    new Set(documents.map(getCycleTagFromDocument).filter(Boolean) as string[]),
  ).filter((tag) => tag !== DOCUMENT_CURRENT_CYCLE_TAG);
  const countByType: Record<string, number> = {};
  for (const tag of cycleTags) {
    const { type } = parseCycleTag(tag);
    countByType[type] = (countByType[type] ?? 0) + 1;
  }
  cycleTags
    .sort((a, b) => a.localeCompare(b))
    .forEach((tag) => {
      options.push({
        id: tag,
        label: formatCycleFilterLabel(tag, countByType, t),
        icon: Calendar,
      });
    });

  return options;
}

function documentMatchesFilter(
  doc: Document | undefined,
  filterId: string,
  status?: DocumentStatus,
) {
  if (filterId === "all") return true;
  if (!doc) return false;
  if (filterId.startsWith(DOCUMENT_CYCLE_TAG_PREFIX)) {
    return doc.tags.includes(filterId);
  }
  if (filterId === "requested") {
    return status === "requested" || isDocumentRequested(doc);
  }
  if (filterId === DOCUMENT_ACTION_NEEDED_FILTER_ID) {
    return (
      status === "requested" ||
      status === "not_started" ||
      isDocumentRequested(doc)
    );
  }
  return true;
}

type CategoryMeta = {
  key: DocumentCategory;
  labelKey: string;
  icon: LucideIcon;
  image: number;
  color: string;
  descriptionKey: string;
};

type CategoryFilterMeta = Omit<CategoryMeta, "key" | "image"> & {
  key: DocumentFilterCategory;
  image?: number;
};

// ── Categories ──

const CATEGORIES: CategoryMeta[] = [
  {
    key: "medical_records",
    labelKey: "documents.category.medical_records.label",
    icon: FileText,
    image: require("./care/assets/icon-folder.webp"),
    color: EMBER.colors.coral,
    descriptionKey: "documents.category.medical_records.description",
  },
  {
    key: "coverage_benefits",
    labelKey: "documents.category.coverage_benefits.label",
    icon: ShieldCheck,
    image: require("./care/assets/icon-shield.webp"),
    color: EMBER.colors.green,
    descriptionKey: "documents.category.coverage_benefits.description",
  },
  {
    key: "admin",
    labelKey: "documents.category.admin.label",
    icon: File,
    image: require("./care/assets/icon-receipt.webp"),
    color: EMBER.colors.gold,
    descriptionKey: "documents.category.admin.description",
  },
  {
    key: "legal_consent",
    labelKey: "documents.category.legal_consent.label",
    icon: ClipboardList,
    image: require("./care/assets/icon-signature.webp"),
    color: EMBER.colors.warm,
    descriptionKey: "documents.category.legal_consent.description",
  },
];

const UNCATEGORIZED_CATEGORY: CategoryFilterMeta = {
  key: "uncategorized",
  labelKey: "documents.category.uncategorized.label",
  icon: File,
  color: EMBER.colors.textFaint,
  descriptionKey: "documents.category.uncategorized.description",
};

const DOCUMENT_CATEGORY_ALIASES: Record<string, DocumentFilterCategory> = {
  medical: "medical_records",
  medical_records: "medical_records",
  "medical records": "medical_records",
  coverage: "coverage_benefits",
  coverage_benefits: "coverage_benefits",
  "coverage & benefits": "coverage_benefits",
  "insurance & coverage": "coverage_benefits",
  insurance: "coverage_benefits",
  legal: "legal_consent",
  legal_consent: "legal_consent",
  "legal & consent": "legal_consent",
  "forms & agreements": "legal_consent",
  forms: "legal_consent",
  admin: "admin",
  "bills & receipts": "admin",
  "bills & payments": "admin",
  bills: "admin",
  uncategorized: "uncategorized",
};

function normalizeDocumentCategory(
  value: string | null | undefined,
): DocumentFilterCategory | null {
  if (!value) return null;
  return DOCUMENT_CATEGORY_ALIASES[value.trim().toLowerCase()] ?? null;
}

// ── Document Slots ──

const ALL_EXCEPT_EXPLORING: TreatmentIntent[] = [
  "Preservation",
  "Donation",
  "Conception",
  "StartingTreatment",
  "InTreatment",
];

const TREATMENT_ACTIVE: TreatmentIntent[] = [
  "Conception",
  "StartingTreatment",
  "InTreatment",
];

export const DOCUMENT_SLOTS: DocumentSlot[] = [
  // ── Medical Records ──
  {
    id: "amh",
    nameKey: "documents.slot.amh.name",
    whyKey: "documents.slot.amh.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "day3-panel",
    nameKey: "documents.slot.day3_panel.name",
    whyKey: "documents.slot.day3_panel.why",
    category: "medical_records",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "afc-ultrasound",
    nameKey: "documents.slot.afc_ultrasound.name",
    whyKey: "documents.slot.afc_ultrasound.why",
    category: "medical_records",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "hsg-saline",
    nameKey: "documents.slot.hsg_saline.name",
    whyKey: "documents.slot.hsg_saline.why",
    category: "medical_records",
    treatmentIntents: TREATMENT_ACTIVE,
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "carrier-screening",
    nameKey: "documents.slot.carrier_screening.name",
    whyKey: "documents.slot.carrier_screening.why",
    category: "medical_records",
    treatmentIntents: [
      "Conception",
      "Preservation",
      "StartingTreatment",
      "InTreatment",
    ],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "pgt-results",
    nameKey: "documents.slot.pgt_results.name",
    whyKey: "documents.slot.pgt_results.why",
    category: "medical_records",
    treatmentIntents: ["StartingTreatment", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "semen-analysis",
    nameKey: "documents.slot.semen_analysis.name",
    whyKey: "documents.slot.semen_analysis.why",
    category: "medical_records",
    treatmentIntents: [
      "Preservation",
      "Conception",
      "StartingTreatment",
      "InTreatment",
    ],
    partnerChoices: ["with-partner"],
    conditions: null,
    priority: "essential",
  },
  {
    id: "thyroid-panel",
    nameKey: "documents.slot.thyroid_panel.name",
    whyKey: "documents.slot.thyroid_panel.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: ["thyroid"],
    priority: "essential",
  },
  {
    id: "pcos-labs",
    nameKey: "documents.slot.pcos_labs.name",
    whyKey: "documents.slot.pcos_labs.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: ["pcos"],
    priority: "recommended",
  },
  {
    id: "endo-imaging",
    nameKey: "documents.slot.endo_imaging.name",
    whyKey: "documents.slot.endo_imaging.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: ["endo"],
    priority: "recommended",
  },
  {
    id: "genetic-counseling",
    nameKey: "documents.slot.genetic_counseling.name",
    whyKey: "documents.slot.genetic_counseling.why",
    category: "medical_records",
    treatmentIntents: TREATMENT_ACTIVE,
    partnerChoices: null,
    conditions: null,
    priority: "optional",
  },
  {
    id: "treatment-plan",
    nameKey: "documents.slot.treatment_plan.name",
    whyKey: "documents.slot.treatment_plan.why",
    category: "medical_records",
    treatmentIntents: ["StartingTreatment", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },

  // ── Coverage & Benefits ──
  {
    id: "insurance-card",
    nameKey: "documents.slot.insurance_card.name",
    whyKey: "documents.slot.insurance_card.why",
    category: "coverage_benefits",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "benefits-summary",
    nameKey: "documents.slot.benefits_summary.name",
    whyKey: "documents.slot.benefits_summary.why",
    category: "coverage_benefits",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "pre-auth",
    nameKey: "documents.slot.pre_auth.name",
    whyKey: "documents.slot.pre_auth.why",
    category: "coverage_benefits",
    treatmentIntents: ["StartingTreatment", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "eob",
    nameKey: "documents.slot.eob.name",
    whyKey: "documents.slot.eob.why",
    category: "coverage_benefits",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "appeal-letter",
    nameKey: "documents.slot.appeal_letter.name",
    whyKey: "documents.slot.appeal_letter.why",
    category: "coverage_benefits",
    treatmentIntents: ["StartingTreatment", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "optional",
  },

  // ── Legal & Consent ──
  {
    id: "consent-forms",
    nameKey: "documents.slot.consent_forms.name",
    whyKey: "documents.slot.consent_forms.why",
    category: "legal_consent",
    treatmentIntents: ["StartingTreatment", "InTreatment", "Preservation"],
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "referral-letter",
    nameKey: "documents.slot.referral_letter.name",
    whyKey: "documents.slot.referral_letter.why",
    category: "legal_consent",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "donor-agreement",
    nameKey: "documents.slot.donor_agreement.name",
    whyKey: "documents.slot.donor_agreement.why",
    category: "legal_consent",
    treatmentIntents: TREATMENT_ACTIVE,
    partnerChoices: ["using-donor"],
    conditions: null,
    priority: "essential",
  },
  {
    id: "partner-consent",
    nameKey: "documents.slot.partner_consent.name",
    whyKey: "documents.slot.partner_consent.why",
    category: "legal_consent",
    treatmentIntents: [
      "Conception",
      "Preservation",
      "StartingTreatment",
      "InTreatment",
    ],
    partnerChoices: ["with-partner"],
    conditions: null,
    priority: "essential",
  },
  {
    id: "storage-agreement",
    nameKey: "documents.slot.storage_agreement.name",
    whyKey: "documents.slot.storage_agreement.why",
    category: "legal_consent",
    treatmentIntents: ["Preservation", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "disposition-directive",
    nameKey: "documents.slot.disposition_directive.name",
    whyKey: "documents.slot.disposition_directive.why",
    category: "legal_consent",
    treatmentIntents: ["Preservation", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },

  // ── Bills & Payments ──
  {
    id: "clinic-invoices",
    nameKey: "documents.slot.clinic_invoices.name",
    whyKey: "documents.slot.clinic_invoices.why",
    category: "admin",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "pharmacy-receipts",
    nameKey: "documents.slot.pharmacy_receipts.name",
    whyKey: "documents.slot.pharmacy_receipts.why",
    category: "admin",
    treatmentIntents: ["StartingTreatment", "InTreatment", "Preservation"],
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "storage-invoices",
    nameKey: "documents.slot.storage_invoices.name",
    whyKey: "documents.slot.storage_invoices.why",
    category: "admin",
    treatmentIntents: ["Preservation", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "lab-bills",
    nameKey: "documents.slot.lab_bills.name",
    whyKey: "documents.slot.lab_bills.why",
    category: "admin",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "travel-receipts",
    nameKey: "documents.slot.travel_receipts.name",
    whyKey: "documents.slot.travel_receipts.why",
    category: "admin",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "optional",
  },
];

const PRIORITY_ORDER = { essential: 0, recommended: 1, optional: 2 };
const DOCUMENT_SLOT_ORDER = (a: DocumentSlot, b: DocumentSlot) =>
  PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];

function deriveStatuses(
  documents:
    | {
        tags: string[];
        uploads: { uploadStatus: "pending" | "uploaded" | null }[];
      }[]
    | undefined,
  slotIds: string[],
): Record<string, DocumentStatus> {
  const statuses: Record<string, DocumentStatus> = {};
  if (!documents) return statuses;

  for (const slotId of slotIds) {
    const matchingDoc = documents.find((doc) => doc.tags.includes(slotId));
    if (!matchingDoc) {
      statuses[slotId] = "not_started";
    } else if (matchingDoc.uploads.some((u) => u.uploadStatus === "uploaded")) {
      statuses[slotId] = "have_it";
    } else if (matchingDoc.uploads.length > 0) {
      statuses[slotId] = "requested";
    } else {
      statuses[slotId] = "not_started";
    }
  }
  return statuses;
}

// ── Components ──

const ALL_DOCUMENT_TYPES: DocumentType[] = [
  "lab",
  "imaging",
  "treatment_plan",
  "genetic",
  "other",
];

function DocumentTypePicker({
  slotId,
  overrideType,
  onChangeType,
}: {
  slotId?: string;
  overrideType?: DocumentType;
  onChangeType?: (type: DocumentType) => void;
}) {
  const capturePress = usePressCapture("DocumentTypePicker");
  const t = useTranslations();
  const [pickerVisible, setPickerVisible] = useState(false);
  const docType =
    overrideType ?? ((slotId && SLOT_DOCUMENT_TYPE[slotId]) || "other");
  const meta = DOCUMENT_TYPE_META[docType];
  const Icon = meta.icon;
  const label = t(meta.labelKey);
  const tag = <Tag label={label} tone="neutral" icon={Icon} />;

  return (
    <>
      {onChangeType ? (
        <Pressable
          accessibilityLabel={label}
          accessibilityRole="button"
          onPress={() => {
            capturePress(pressLabel(label), "open_document_type_picker");
            setPickerVisible(true);
          }}
          hitSlop={6}
          style={{ zIndex: 10 }}
          // Prevent parent Pressable from also handling the press on web
          {...(Platform.OS === "web"
            ? { onStartShouldSetResponder: () => true }
            : {})}
        >
          {tag}
        </Pressable>
      ) : (
        tag
      )}

      {onChangeType && (
        <Modal
          visible={pickerVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setPickerVisible(false)}
        >
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel("dismiss_document_type_picker"),
                "dismiss_document_type_picker",
              );
              setPickerVisible(false);
            }}
            style={{
              flex: 1,
              backgroundColor: "rgba(0,0,0,0.3)",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Pressable
              onPress={(e) => e.stopPropagation?.()}
              style={{
                backgroundColor: "#fff",
                borderRadius: 14,
                padding: 8,
                width: 220,
                ...Platform.select({
                  ios: {
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.15,
                    shadowRadius: 12,
                  },
                  android: { elevation: 8 },
                  web: { boxShadow: "0 4px 24px rgba(0,0,0,0.12)" },
                }),
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                }}
              >
                {t("documents.doc_type.header")}
              </Text>
              {ALL_DOCUMENT_TYPES.map((type) => {
                const typeMeta = DOCUMENT_TYPE_META[type];
                const TypeIcon = typeMeta.icon;
                const isSelected = type === docType;
                return (
                  <Pressable
                    key={type}
                    onPress={() => {
                      capturePress(
                        pressLabel(t(typeMeta.labelKey)),
                        "document_type",
                      );
                      onChangeType(type);
                      setPickerVisible(false);
                    }}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 10,
                      paddingHorizontal: 10,
                      paddingVertical: 10,
                      borderRadius: 8,
                      backgroundColor: isSelected
                        ? `${typeMeta.color}10`
                        : "transparent",
                    }}
                  >
                    <View
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        backgroundColor: `${typeMeta.color}14`,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TypeIcon
                        size={14}
                        color={typeMeta.color}
                        strokeWidth={1.5}
                      />
                    </View>
                    <Text
                      className="font-dm-sans"
                      style={{
                        flex: 1,
                        fontSize: 13,
                        fontWeight: isSelected ? "700" : "500",
                        color: isSelected ? typeMeta.color : EMBER.colors.text,
                      }}
                    >
                      {t(typeMeta.labelKey)}
                    </Text>
                    {isSelected && (
                      <Check
                        size={14}
                        color={typeMeta.color}
                        strokeWidth={2.5}
                      />
                    )}
                  </Pressable>
                );
              })}
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </>
  );
}

function StatusTag({
  status,
  slotId,
  overrideType,
  onChangeType,
}: {
  status: DocumentStatus;
  slotId?: string;
  overrideType?: DocumentType;
  onChangeType?: (type: DocumentType) => void;
}) {
  const t = useTranslations();
  if (status === "have_it") {
    // Show document type tag only for medical records; others get "On file"
    if (slotId || overrideType || onChangeType) {
      return (
        <DocumentTypePicker
          slotId={slotId}
          overrideType={overrideType}
          onChangeType={onChangeType}
        />
      );
    }
    return (
      <StatusBadge
        label={t("documents.status.on_file")}
        tone="green"
        icon={FileText}
      />
    );
  }
  if (status === "requested") {
    return (
      <StatusBadge
        label={t("documents.status.requested")}
        tone="gold"
        icon={Calendar}
      />
    );
  }
  return (
    <StatusBadge
      label={t("documents.status.missing")}
      tone="coral"
      icon={AlertCircle}
    />
  );
}

const FLAG_COLORS: Record<string, string> = {
  normal: EMBER.colors.green,
  low: EMBER.colors.gold,
  high: EMBER.colors.coral,
  critical: EMBER.colors.coral,
};

function DocumentCard({
  slot,
  status,
  recordDate,
  clinicalValues,
  medications,
  appointments,
  overrideType,
  onChangeType,
  onPress,
}: {
  slot: DocumentSlot;
  status: DocumentStatus;
  recordDate?: string | null;
  clinicalValues?: ClinicalValue[] | null;
  medications?: MedicationInfo[];
  appointments?: AppointmentInfo[];
  overrideType?: DocumentType;
  onChangeType?: (type: DocumentType) => void;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("DocumentCard");
  const t = useTranslations();
  // Show the first (primary) clinical value if available
  const primaryValue = clinicalValues?.[0] ?? null;

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(t(slot.nameKey)), "open_document_slot");
        onPress();
      }}
      style={{
        flex: 1,
        ...GLASS_CARD_STYLE,
        padding: 14,
        minHeight: 110,
        justifyContent: "space-between",
      }}
    >
      {/* Top: icon + status tag */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <View
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            backgroundColor: `${EMBER.colors.warm}10`,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FileText size={16} color={EMBER.colors.warm} strokeWidth={1.5} />
        </View>
        <StatusTag
          status={status}
          slotId={slot.id}
          overrideType={overrideType}
          onChangeType={onChangeType}
        />
      </View>

      {/* Bottom: name + clinical value + date */}
      <View style={{ marginTop: 10 }}>
        <Text
          className="font-dm-sans"
          numberOfLines={2}
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: EMBER.colors.text,
            lineHeight: 17,
          }}
        >
          {t(slot.nameKey)}
        </Text>

        {/* Clinical value — shown when a parsed result is available */}
        {primaryValue && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "baseline",
              gap: 4,
              marginTop: 6,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 18,
                fontWeight: "700",
                color: primaryValue.flag
                  ? (FLAG_COLORS[primaryValue.flag] ?? EMBER.colors.text)
                  : EMBER.colors.text,
                lineHeight: 22,
              }}
            >
              {primaryValue.value}
            </Text>
            {primaryValue.unit && (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  color: EMBER.colors.textLight,
                  fontWeight: "500",
                }}
              >
                {primaryValue.unit}
              </Text>
            )}
          </View>
        )}

        {/* Medications — shown for treatment plans / prescriptions */}
        {!primaryValue && medications && medications.length > 0 && (
          <View style={{ marginTop: 6, gap: 3 }}>
            {medications.slice(0, 3).map((med, i) => (
              <View
                key={i}
                style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
              >
                <Pill size={10} color={EMBER.colors.gold} strokeWidth={1.5} />
                <Text
                  className="font-dm-sans"
                  numberOfLines={1}
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                    flex: 1,
                  }}
                >
                  {med.name}
                  {med.detail ? (
                    <Text
                      style={{
                        fontWeight: "400",
                        color: EMBER.colors.textLight,
                      }}
                    >
                      {" "}
                      {med.detail}
                    </Text>
                  ) : null}
                  {med.frequency || med.timeOfDay ? (
                    <Text
                      style={{
                        fontWeight: "400",
                        fontSize: 10,
                        color: EMBER.colors.textFaint,
                      }}
                    >
                      {" · "}
                      {[med.frequency, med.timeOfDay]
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  ) : null}
                </Text>
              </View>
            ))}
            {medications.length > 3 && (
              <Text
                className="font-dm-sans"
                style={{ fontSize: 10, color: EMBER.colors.textFaint }}
              >
                {t("documents.more_count", {
                  params: { count: String(medications.length - 3) },
                })}
              </Text>
            )}
          </View>
        )}

        {/* Appointments — shown for treatment plans / prescriptions */}
        {!primaryValue && appointments && appointments.length > 0 && (
          <View style={{ marginTop: medications?.length ? 4 : 6, gap: 3 }}>
            {appointments.slice(0, 2).map((appt, i) => (
              <View
                key={i}
                style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
              >
                <Calendar
                  size={10}
                  color={EMBER.colors.coral}
                  strokeWidth={1.5}
                />
                <Text
                  className="font-dm-sans"
                  numberOfLines={1}
                  style={{
                    fontSize: 11,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                    flex: 1,
                  }}
                >
                  {appt.title}
                  {appt.date ? (
                    <Text
                      style={{
                        fontWeight: "400",
                        color: EMBER.colors.textLight,
                      }}
                    >
                      {" "}
                      {appt.date}
                    </Text>
                  ) : null}
                </Text>
              </View>
            ))}
            {appointments.length > 2 && (
              <Text
                className="font-dm-sans"
                style={{ fontSize: 10, color: EMBER.colors.textFaint }}
              >
                {t("documents.more_count", {
                  params: { count: String(appointments.length - 2) },
                })}
              </Text>
            )}
          </View>
        )}

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginTop: primaryValue ? 4 : 4,
          }}
        >
          <Calendar
            size={10}
            color={EMBER.colors.textFaint}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 11, color: EMBER.colors.textFaint }}
          >
            {recordDate ?? t("documents.date_unknown")}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

function StaticDocumentRailChip({
  label,
  icon: Icon,
}: {
  label: string;
  icon: LucideIcon;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 20,
        borderWidth: 1,
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        borderColor: "rgba(0, 0, 0, 0.06)",
      }}
    >
      <Icon size={14} color={EMBER.colors.textMid} style={{ marginRight: 6 }} />
      <Text
        style={{
          fontFamily: "DMSans_400Regular",
          fontSize: 13,
          fontWeight: "400",
          color: EMBER.colors.textMid,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function MovingDocumentChipRail({
  pills,
}: {
  pills: { label: string; icon: LucideIcon }[];
}) {
  const scrollRef = useRef<ScrollView | null>(null);
  const offsetRef = useRef(0);
  const [sequenceWidth, setSequenceWidth] = useState(0);

  useEffect(() => {
    if (sequenceWidth === 0) return;

    const interval = setInterval(() => {
      let nextOffset = offsetRef.current + 0.65;
      if (nextOffset >= sequenceWidth) {
        nextOffset = 0;
      }

      offsetRef.current = nextOffset;
      scrollRef.current?.scrollTo({ x: nextOffset, animated: false });
    }, 24);

    return () => clearInterval(interval);
  }, [sequenceWidth]);

  const renderPillSet = (copyIndex: number, measure = false) => (
    <View
      key={copyIndex}
      onLayout={
        measure
          ? (event) => setSequenceWidth(event.nativeEvent.layout.width)
          : undefined
      }
      style={{
        flexDirection: "row",
        gap: 8,
        paddingRight: 8,
      }}
    >
      {pills.map((pill) => (
        <StaticDocumentRailChip
          key={`${copyIndex}-${pill.label}`}
          label={pill.label}
          icon={pill.icon}
        />
      ))}
    </View>
  );

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        flexDirection: "row",
        paddingHorizontal: 2,
      }}
      style={{ width: "100%" }}
    >
      {renderPillSet(0, true)}
      {renderPillSet(1)}
    </ScrollView>
  );
}

function DocumentsEmptyState({
  variant,
  onUpload,
  onFilesSelected,
  onClearFilters,
}: {
  variant: "empty" | "filtered";
  onUpload: () => void;
  onFilesSelected: (files: SelectedFile[]) => void;
  onClearFilters: () => void;
}) {
  const capturePress = usePressCapture("DocumentsEmptyState");
  const t = useTranslations();
  const isFiltered = variant === "filtered";
  const documentTypePills = [
    ...DOCUMENT_UPLOAD_CATEGORIES.map((category) => ({
      label: t(category.labelKey),
      icon: category.Icon,
    })),
    { label: t("documents.empty.chip_full_records"), icon: Layers },
    { label: t("documents.empty.chip_lab_results"), icon: Microscope },
    { label: t("documents.empty.chip_ultrasounds"), icon: ScanLine },
    { label: t("documents.empty.chip_prescriptions"), icon: Pill },
    { label: t("documents.empty.chip_appointments"), icon: Calendar },
    { label: t("documents.empty.chip_genetics"), icon: Dna },
    { label: t("documents.empty.chip_quotes"), icon: FileText },
    { label: t("documents.empty.chip_receipts"), icon: Receipt },
    { label: t("documents.empty.chip_referrals"), icon: PenTool },
  ];

  if (isFiltered) {
    return (
      <View
        style={{
          ...GLASS_CARD_STYLE,
          padding: 24,
          alignItems: "center",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            lineHeight: 20,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
          }}
        >
          {t("documents.empty.no_matches")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textLight,
            textAlign: "center",
            lineHeight: 18,
            marginTop: 4,
            marginBottom: 14,
          }}
        >
          {t("documents.empty.adjust_filters")}
        </Text>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("documents.empty.clear_filters")),
              "documents.empty.clear_filters",
            );
            onClearFilters();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            backgroundColor: EMBER.colors.warm,
            paddingHorizontal: 20,
            paddingVertical: 10,
            borderRadius: 10,
          }}
        >
          <X size={14} color="#fff" strokeWidth={2.2} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 13, fontWeight: "600", color: "#fff" }}
          >
            {t("documents.empty.clear_filters")}
          </Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View
      style={{
        paddingVertical: 8,
        alignItems: "center",
        overflow: "visible",
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          lineHeight: 27,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
        }}
      >
        {t("documents.empty.upload_anything_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textMid,
          textAlign: "center",
          lineHeight: 21,
          marginTop: 8,
          maxWidth: 410,
        }}
      >
        {t("documents.empty.upload_anything_body")}
      </Text>
      <View
        style={{
          width: "100%",
          marginTop: 22,
          marginBottom: 18,
        }}
      >
        <MovingDocumentChipRail pills={documentTypePills} />
      </View>
      <View style={{ width: "100%" }}>
        <DocumentFileDropZone
          onPick={onUpload}
          onFilesSelected={onFilesSelected}
        />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          color: EMBER.colors.textFaint,
          textAlign: "center",
          lineHeight: 17,
          marginTop: 12,
          maxWidth: 420,
        }}
      >
        {t("documents.empty.upload_anything_note")}
      </Text>
    </View>
  );
}

// ── Main Screen ──

type Props = NativeStackScreenProps<CareStackParamList, "Documents">;

/** Map test results to document slots via shared upload IDs */
function buildSlotTestResults(
  documents:
    | {
        tags: string[];
        uploads: { id: string; uploadStatus: "pending" | "uploaded" | null }[];
      }[]
    | undefined,
  testResults: TestResult[] | undefined,
): Record<string, ClinicalValue[]> {
  const map: Record<string, ClinicalValue[]> = {};
  if (!documents || !testResults) return map;

  // Build a lookup: uploadId → slotId
  const uploadToSlot: Record<string, string> = {};
  for (const doc of documents) {
    const slotTag = doc.tags[0];
    if (!slotTag) continue;
    for (const upload of doc.uploads) {
      if (upload.id) {
        uploadToSlot[upload.id] = slotTag;
      }
    }
  }

  // Match test results to slots via their attachment uploadId
  for (const tr of testResults) {
    const uploadId = tr.attachment?.uploadId;
    if (!uploadId) continue;
    const slotId = uploadToSlot[uploadId];
    if (!slotId) continue;

    const value = tr.patientValue ?? tr.value ?? tr.doctorValue;
    if (!value) continue;

    const flag =
      tr.clinicalAssessment === "unknown"
        ? undefined
        : (tr.clinicalAssessment ?? undefined);

    if (!map[slotId]) {
      map[slotId] = [];
    }
    map[slotId].push({
      label: tr.name ?? "",
      value,
      unit: tr.unit ?? undefined,
      flag: flag as ClinicalValue["flag"],
    });
  }

  return map;
}

/** Extract medications from documents matched to slots */
function buildSlotMedications(
  documents: { tags: string[] }[] | undefined,
): Record<string, MedicationInfo[]> {
  const map: Record<string, MedicationInfo[]> = {};
  if (!documents) return map;
  for (const doc of documents) {
    const slotTag = doc.tags[0];
    if (!slotTag) continue;
    const meds = getMedicationsFromTags(doc.tags);
    if (meds.length > 0) {
      map[slotTag] = meds;
    }
  }
  return map;
}

function buildSlotAppointments(
  documents: { tags: string[] }[] | undefined,
): Record<string, AppointmentInfo[]> {
  const map: Record<string, AppointmentInfo[]> = {};
  if (!documents) return map;
  for (const doc of documents) {
    const slotTag = doc.tags[0];
    if (!slotTag) continue;
    const appts = getAppointmentsFromTags(doc.tags);
    if (appts.length > 0) {
      map[slotTag] = appts;
    }
  }
  return map;
}

/** Get the record date for a slot from its matching document */
function buildSlotDates(
  documents:
    | {
        tags: string[];
        documentDate: string | null;
        createdAt: string | null;
      }[]
    | undefined,
): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  if (!documents) return map;
  for (const doc of documents) {
    const slotTag = doc.tags[0];
    if (!slotTag) continue;
    map[slotTag] = doc.documentDate ?? doc.createdAt ?? null;
  }
  return map;
}

function getDocumentRowStatus(status: DocumentStatus): DocumentRowStatus {
  if (status === "have_it") return "on-file";
  if (status === "requested") return "requested";
  return "missing";
}

function shouldUseDocumentRow(category: DocumentFilterCategory) {
  return (
    category === "medical_records" ||
    category === "legal_consent" ||
    category === "coverage_benefits" ||
    category === "admin" ||
    category === "uncategorized"
  );
}

function formatDocumentRowDate(
  date: string | null | undefined,
  t: TranslationFunction,
  locale: string,
) {
  if (!date) return t("documents.no_date");
  const normalized = date.includes("T") ? date : `${date}T00:00:00`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return t("documents.no_date");
  const tag = locale === "fr" ? "fr-FR" : "en-US";
  return parsed.toLocaleDateString(tag, {
    month: "short",
    day: "numeric",
  });
}

function getClinicalValueCategory(
  flag: ClinicalValue["flag"] | undefined,
): ResultInterpretation["category"] {
  if (flag === "low") return "low";
  if (flag === "high" || flag === "critical") return "high";
  return "normal";
}

function getClinicalValuePurpose(
  slot: DocumentSlot,
  value: ClinicalValue,
  t: TranslationFunction,
) {
  const normalized = `${value.label} ${slot.nameKey}`.toLowerCase();
  if (normalized.includes("amh")) {
    return t("documents.clinical_value.purpose.amh");
  }
  if (normalized.includes("fsh")) {
    return t("documents.clinical_value.purpose.fsh");
  }
  if (normalized.includes("e2") || normalized.includes("estradiol")) {
    return t("documents.clinical_value.purpose.e2");
  }
  if (normalized.includes("afc") || normalized.includes("follicle")) {
    return t("documents.clinical_value.purpose.afc");
  }
  if (normalized.includes("tsh") || normalized.includes("thyroid")) {
    return t("documents.clinical_value.purpose.tsh");
  }
  return slot.whyKey;
}

function clinicalValueToInsight(
  slot: DocumentSlot,
  value: ClinicalValue,
  t: TranslationFunction,
): ResultInterpretation {
  const category = getClinicalValueCategory(value.flag);
  return {
    testName: value.label || slot.nameKey,
    testPurpose: getClinicalValuePurpose(slot, value, t),
    value: value.value || t("care.cycle_tests.see_details"),
    unit: value.unit ?? "",
    category,
    headline: value.label || slot.nameKey,
    interpretation: slot.whyKey,
    whatThisMeans: value.referenceRange
      ? t("documents.clinical_value.reference_range", {
          params: { range: value.referenceRange },
        })
      : slot.whyKey,
  };
}

type FlatDocumentItem =
  | {
      kind: "slot";
      id: string;
      category: DocumentCategory;
      slot: DocumentSlot;
      status: DocumentStatus;
      document?: Document;
      date: string | null;
      actionNeeded: boolean;
      searchText: string;
      sortLabel: string;
    }
  | {
      kind: "free";
      id: string;
      category: DocumentFilterCategory;
      document: Document;
      date: string | null;
      actionNeeded: boolean;
      searchText: string;
      sortLabel: string;
    };

function getCategoryMeta(category: DocumentFilterCategory): CategoryFilterMeta {
  return (
    CATEGORIES.find((cat) => cat.key === category) ?? UNCATEGORIZED_CATEGORY
  );
}

function getDocumentCategoryFromTags(tags: string[]): DocumentFilterCategory {
  const category = tags
    .map((tag) => normalizeDocumentCategory(tag))
    .find(
      (tag): tag is DocumentCategory => Boolean(tag) && tag !== "uncategorized",
    );
  return category ?? "uncategorized";
}

function compareFlatDocumentItems(a: FlatDocumentItem, b: FlatDocumentItem) {
  if (a.actionNeeded !== b.actionNeeded) return a.actionNeeded ? -1 : 1;
  const dateA = a.date ?? "";
  const dateB = b.date ?? "";
  if (dateA && dateB && dateA !== dateB) return dateB.localeCompare(dateA);
  if (dateA && !dateB) return -1;
  if (!dateA && dateB) return 1;
  return a.sortLabel.localeCompare(b.sortLabel);
}

function flatDocumentItemMatchesFilter(
  item: FlatDocumentItem,
  filterId: string,
) {
  if (filterId === DOCUMENT_ACTION_NEEDED_FILTER_ID) return item.actionNeeded;
  if (item.kind === "slot") {
    return documentMatchesFilter(item.document, filterId, item.status);
  }
  return documentMatchesFilter(
    item.document,
    filterId,
    item.actionNeeded ? "requested" : "have_it",
  );
}

function isMoonDocumentsPreviewEnabled() {
  if (!__DEV__ || typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("dev_preview_documents") === "true" ||
    params.get("dev_preview_all_cards") === "true"
  );
}

function getDocumentUploadPrototypeState(): DocumentUploadPrototypeState | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const state = new URLSearchParams(window.location.search).get(
    "dev_documents_upload_prototype",
  );
  return DOCUMENT_UPLOAD_PROTOTYPE_STATES.includes(
    state as DocumentUploadPrototypeState,
  )
    ? (state as DocumentUploadPrototypeState)
    : null;
}

function isMoonDocumentsEmptyPreviewEnabled() {
  if (!__DEV__ || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("dev_documents_state") ===
    "empty"
  );
}

function getMoonDocumentsPreviewCategory(): DocumentCategory | undefined {
  if (!__DEV__ || typeof window === "undefined") return undefined;
  const category = new URLSearchParams(window.location.search).get(
    "dev_documents_category",
  );
  if (
    category === "medical_records" ||
    category === "coverage_benefits" ||
    category === "legal_consent" ||
    category === "admin"
  ) {
    return category;
  }
  return undefined;
}

function createPreviewDocument(
  id: string,
  name: string,
  tags: string[],
  options: {
    documentDate: string;
    uploadStatus?: "pending" | "uploaded";
    uploadName?: string;
  },
): Document {
  const uploadId = `${id}-upload`;
  return {
    id,
    patientId: "dev-patient",
    name,
    documentDate: options.documentDate,
    tags,
    sharedWith: ["patient"],
    createdBy: "dev-preview",
    createdAt: `${options.documentDate}T09:00:00.000Z`,
    updatedBy: null,
    updatedAt: null,
    uploads: [
      {
        id: uploadId,
        name: options.uploadName ?? `${name}.pdf`,
        url: null,
        uploadStatus: options.uploadStatus ?? "uploaded",
        contentType: "application/pdf",
        size: "512000",
        notes: null,
        createdAt: `${options.documentDate}T09:00:00.000Z`,
      },
    ],
  };
}

function createPreviewTestResult(
  id: string,
  uploadId: string,
  name: string,
  value: string,
  unit: string | null,
  assessment: TestResult["clinicalAssessment"],
): TestResult {
  return {
    id,
    patientId: "dev-patient",
    phaseId: null,
    cycleId: "dev-cycle",
    name,
    type: "blood",
    bloodTestCategory: "hormonal",
    urineTestCategory: null,
    geneticsTestCategory: null,
    imagingTestCategory: null,
    samplingTestCategory: null,
    clinicalTestCategory: null,
    semenFluidTestCategory: null,
    biopsyTestCategory: null,
    otherCategory: null,
    patientValue: value,
    doctorValue: null,
    value,
    valueType: "numeric",
    unit,
    date: "2026-05-19",
    notes: null,
    patientAssessmentNormal: assessment === "normal",
    clinicalAssessment: assessment,
    clinicalAssessmentDetails: null,
    loincCode: null,
    providerId: null,
    ownership: "patient",
    ownerId: "dev-patient",
    sourceId: null,
    audience: ["patient"],
    createdAt: "2026-05-19T09:00:00.000Z",
    updatedAt: "2026-05-19T09:00:00.000Z",
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
    attachment: {
      id: `${id}-attachment`,
      testResultId: id,
      uploadId,
      createdAt: "2026-05-19T09:00:00.000Z",
      updatedAt: "2026-05-19T09:00:00.000Z",
      updatedBy: null,
      upload: {
        id: uploadId,
        name: `${name}.pdf`,
        contentDigest: null,
        url: null,
        uploadStatus: "uploaded",
        contentType: "application/pdf",
        size: "512000",
        notes: null,
        ownership: "patient",
        ownerId: "dev-patient",
        sourceId: null,
        audience: ["patient"],
        createdAt: "2026-05-19T09:00:00.000Z",
        updatedAt: "2026-05-19T09:00:00.000Z",
        updatedBy: null,
      },
    },
    phase: null,
  };
}

const MOON_DOCUMENTS_PREVIEW_DOCUMENTS: Document[] = [
  createPreviewDocument(
    "doc-amh",
    "AMH blood test",
    ["amh", "medical_records", "doctype:lab", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-12",
    },
  ),
  createPreviewDocument(
    "doc-day3",
    "Day 3 hormone panel",
    [
      "day3-panel",
      "medical_records",
      "doctype:lab",
      DOCUMENT_CURRENT_CYCLE_TAG,
    ],
    {
      documentDate: "2026-05-10",
    },
  ),
  createPreviewDocument(
    "doc-afc",
    "AFC ultrasound report",
    [
      "afc-ultrasound",
      "medical_records",
      "doctype:imaging",
      DOCUMENT_CURRENT_CYCLE_TAG,
    ],
    {
      documentDate: "2026-05-10",
    },
  ),
  createPreviewDocument(
    "doc-treatment-plan",
    "Treatment plan / protocol",
    [
      "treatment-plan",
      "medical_records",
      "doctype:treatment_plan",
      DOCUMENT_CURRENT_CYCLE_TAG,
      "med:Gonal-F|150 IU|Daily|May 16|8 PM",
      "med:Cetrotide|0.25 mg|Daily|May 19|8 AM",
      "med:Menopur|75 IU|Daily|May 16|8 PM",
      "appt:Monitoring ultrasound|Monitoring|May 20",
      "appt:Trigger instruction call|Care team|May 22",
    ],
    {
      documentDate: "2026-05-16",
    },
  ),
  createPreviewDocument(
    "doc-carrier",
    "Carrier screening results",
    ["carrier-screening", "medical_records", "doctype:genetic", "cycle:ivf-1"],
    {
      documentDate: "2026-05-18",
      uploadStatus: "pending",
    },
  ),
  createPreviewDocument(
    "doc-insurance-card",
    "Insurance card",
    ["insurance-card", "coverage_benefits"],
    {
      documentDate: "2026-04-20",
    },
  ),
  createPreviewDocument(
    "doc-benefits",
    "Fertility benefits summary",
    ["benefits-summary", "coverage_benefits"],
    {
      documentDate: "2026-04-22",
    },
  ),
  createPreviewDocument(
    "doc-preauth",
    "Pre-authorization letter",
    ["pre-auth", "coverage_benefits", "cycle:ivf-1"],
    {
      documentDate: "2026-05-17",
      uploadStatus: "pending",
    },
  ),
  createPreviewDocument(
    "doc-consent",
    "Treatment consent forms",
    ["consent-forms", "legal_consent", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-15",
    },
  ),
  createPreviewDocument(
    "doc-partner-consent",
    "Partner consent forms",
    ["partner-consent", "legal_consent", "cycle:ivf-1"],
    {
      documentDate: "2026-05-18",
      uploadStatus: "pending",
    },
  ),
  createPreviewDocument(
    "doc-storage-agreement",
    "Cryostorage agreement",
    ["storage-agreement", "legal_consent", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-13",
    },
  ),
  createPreviewDocument(
    "doc-clinic-invoice",
    "Clinic invoice",
    ["clinic-invoices", "admin", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-11",
    },
  ),
  createPreviewDocument(
    "doc-pharmacy-receipt",
    "Pharmacy receipt",
    ["pharmacy-receipts", "admin", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-16",
    },
  ),
  createPreviewDocument(
    "doc-lab-bill",
    "Outside lab bill",
    ["lab-bills", "admin", DOCUMENT_UPLOADED_BY_USER_TAG],
    {
      documentDate: "2026-05-03",
    },
  ),
  createPreviewDocument(
    "doc-free-coverage",
    "Progyny authorization packet",
    ["coverage_benefits", DOCUMENT_UPLOADED_BY_USER_TAG],
    {
      documentDate: "2026-05-09",
    },
  ),
];

const MOON_DOCUMENTS_PREVIEW_TEST_RESULTS: TestResult[] = [
  createPreviewTestResult(
    "result-amh",
    "doc-amh-upload",
    "AMH",
    "2.8",
    "ng/mL",
    "normal",
  ),
  createPreviewTestResult(
    "result-fsh",
    "doc-day3-upload",
    "FSH",
    "8.1",
    "mIU/mL",
    "normal",
  ),
  createPreviewTestResult(
    "result-afc",
    "doc-afc-upload",
    "AFC",
    "15",
    "follicles",
    "normal",
  ),
];

const EMPTY_DOCUMENTS_PREVIEW: Document[] = [];
const EMPTY_TEST_RESULTS_PREVIEW: TestResult[] = [];

export default function DocumentsScreen({ navigation, route }: Props) {
  const capturePress = usePressCapture("DocumentsScreen");
  const visualEvidenceRoute =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("dev_visual_evidence") ===
      "true";
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const t = useTranslations();
  const { locale } = useLocale();
  const documentsQuery = useDocuments();
  const {
    data: documents,
    isLoading: documentsLoading,
    error: documentsError,
  } = documentsQuery;
  const testResultsQuery = useTestResults();
  const { data: testResults, error: testResultsError } = testResultsQuery;
  const authorizedExperience = useAuthorizedExperienceProjection();
  const isDocumentsPreview = isMoonDocumentsPreviewEnabled();
  const documentUploadPrototypeState = getDocumentUploadPrototypeState();
  const devClinicalDataState = getDevClinicalDataState("documents");
  const documentsDataState = devClinicalDataState
    ? devClinicalDataState
    : isDocumentsPreview
      ? null
      : documentsError && documents === undefined
        ? "unavailable"
        : documentsError || testResultsError
          ? "stale"
          : null;
  const isRetryingDocuments =
    documentsQuery.isFetching || testResultsQuery.isFetching;
  const retryDocuments = () => {
    void documentsQuery.refetch();
    void testResultsQuery.refetch();
  };
  const isEmptyDocumentsPreview =
    isDocumentsPreview && isMoonDocumentsEmptyPreviewEnabled();
  const displayDocuments = useMemo(() => {
    if (!isDocumentsPreview) return documents;
    return isEmptyDocumentsPreview
      ? EMPTY_DOCUMENTS_PREVIEW
      : MOON_DOCUMENTS_PREVIEW_DOCUMENTS;
  }, [documents, isDocumentsPreview, isEmptyDocumentsPreview]);
  const displayTestResults = useMemo(() => {
    if (!isDocumentsPreview) return testResults;
    return isEmptyDocumentsPreview
      ? EMPTY_TEST_RESULTS_PREVIEW
      : MOON_DOCUMENTS_PREVIEW_TEST_RESULTS;
  }, [isDocumentsPreview, isEmptyDocumentsPreview, testResults]);
  const displayDocumentsLoading = isDocumentsPreview ? false : documentsLoading;
  const initialCategory = route.params?.initialCategory;
  const initialDocumentId = route.params?.initialDocumentId;
  const initialDocumentFilter = route.params?.initialDocumentFilter;
  const initialDocumentFilterLabel =
    route.params?.initialDocumentFilterLabel ??
    t("documents.filter.cycle_documents_fallback");
  const previewInitialCategory = isDocumentsPreview
    ? getMoonDocumentsPreviewCategory()
    : undefined;
  const initialCategoryFilter = useMemo(
    () => normalizeDocumentCategory(previewInitialCategory ?? initialCategory),
    [initialCategory, previewInitialCategory],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [activeDocumentFilter, setActiveDocumentFilter] = useState(
    initialDocumentFilter ?? "all",
  );
  const [activeCategoryFilters, setActiveCategoryFilters] = useState<
    DocumentFilterCategory[]
  >(() => (initialCategoryFilter ? [initialCategoryFilter] : []));
  const [uploadModalVisible, setUploadModalVisible] = useState(
    route.params?.openUpload === true,
  );
  const [uploadSlot, setUploadSlot] = useState<DocumentSlotInfo>(null);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<SelectedFile[]>(
    [],
  );
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewDocumentInfo, setPreviewDocumentInfo] =
    useState<PreviewDocumentInfo>(null);
  const openedInitialDocumentId = useRef<string | null>(null);
  const updateDocument = useUpdateDocument();
  const uploadButtonDrift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (route.params?.openUpload === true) {
      setUploadModalVisible(true);
    }
  }, [route.params?.openUpload]);

  useEffect(() => {
    if (!initialDocumentId) {
      openedInitialDocumentId.current = null;
      return;
    }
    if (
      !displayDocuments ||
      openedInitialDocumentId.current === initialDocumentId
    ) {
      return;
    }

    const document = displayDocuments.find(
      (item) => item.id === initialDocumentId,
    );
    if (!document) return;

    const slot = DOCUMENT_SLOTS.find((item) => document.tags.includes(item.id));
    setPreviewDocumentInfo({
      slotId: slot?.id ?? document.id,
      slotName: slot ? t(slot.nameKey) : document.name,
      slotWhy: slot ? t(slot.whyKey) : "",
      document,
    });
    setPreviewModalVisible(true);
    openedInitialDocumentId.current = initialDocumentId;
  }, [displayDocuments, initialDocumentId, t]);

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(uploadButtonDrift, {
          toValue: 1,
          duration: 7000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(uploadButtonDrift, {
          toValue: 0,
          duration: 7000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [uploadButtonDrift]);

  // Optimistic overrides — applied instantly, reconciled when fresh API data arrives
  const [optimisticTypes, setOptimisticTypes] = useState<
    Record<string, DocumentType>
  >({});

  // Reconcile: clear optimistic entries once the API data reflects the same type
  useEffect(() => {
    if (!displayDocuments) return;
    setOptimisticTypes((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      for (const key of keys) {
        // Find the doc by slot tag or by doc ID
        const doc =
          displayDocuments.find((d) => d.tags.includes(key)) ??
          displayDocuments.find((d) => d.id === key);
        if (!doc) continue;
        const persisted = getDocTypeFromTags(doc.tags);
        if (persisted === prev[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [displayDocuments]);

  /** Resolve the document type for a slot: optimistic → persisted tag → slot default */
  const getSlotDocType = useCallback(
    (slotId: string): DocumentType | undefined => {
      if (optimisticTypes[slotId]) return optimisticTypes[slotId];
      if (!displayDocuments) return undefined;
      const doc = displayDocuments.find((d) => d.tags.includes(slotId));
      if (!doc) return undefined;
      return getDocTypeFromTags(doc.tags);
    },
    [displayDocuments, optimisticTypes],
  );

  const handleChangeType = useCallback(
    (slotId: string, type: DocumentType) => {
      if (isDocumentsPreview) {
        setOptimisticTypes((prev) => ({ ...prev, [slotId]: type }));
        return;
      }
      if (!displayDocuments) return;
      const doc = displayDocuments.find((d) => d.tags.includes(slotId));
      if (!doc || doc.readOnly) return;
      setOptimisticTypes((prev) => ({ ...prev, [slotId]: type }));
      const newTags = setDocTypeInTags(doc.tags, type);
      updateDocument.mutate({
        documentId: doc.id,
        data: { name: doc.name, tags: newTags, uploads: [] },
      });
    },
    [displayDocuments, isDocumentsPreview, updateDocument],
  );

  /** Same for free (non-slot) documents, looked up by doc ID */
  const handleChangeFreeDocType = useCallback(
    (docId: string, type: DocumentType) => {
      if (isDocumentsPreview) {
        setOptimisticTypes((prev) => ({ ...prev, [docId]: type }));
        return;
      }
      if (!displayDocuments) return;
      const doc = displayDocuments.find((d) => d.id === docId);
      if (!doc || doc.readOnly) return;
      setOptimisticTypes((prev) => ({ ...prev, [docId]: type }));
      const newTags = setDocTypeInTags(doc.tags, type);
      updateDocument.mutate({
        documentId: doc.id,
        data: { name: doc.name, tags: newTags, uploads: [] },
      });
    },
    [displayDocuments, isDocumentsPreview, updateDocument],
  );

  const filteredSlots = useMemo(
    () =>
      filterDocumentSlotsForExperience(DOCUMENT_SLOTS, {
        status: authorizedExperience.status,
        context: authorizedExperience.projection?.context ?? null,
        capability: authorizedExperience.canonical?.capability ?? null,
      }).sort(DOCUMENT_SLOT_ORDER),
    [
      authorizedExperience.canonical?.capability,
      authorizedExperience.projection?.context,
      authorizedExperience.status,
    ],
  );
  const allSlotIds = useMemo(
    () => new Set(DOCUMENT_SLOTS.map((s) => s.id)),
    [],
  );
  const documentFilterOptions = useMemo(() => {
    const options = buildDocumentFilterOptions(displayDocuments, t);
    if (
      initialDocumentFilter &&
      !options.some((option) => option.id === initialDocumentFilter)
    ) {
      return [
        options[0] ?? {
          id: "all",
          label: t("documents.filter.all"),
          icon: Layers,
        },
        {
          id: initialDocumentFilter,
          label: initialDocumentFilterLabel,
          icon: Calendar,
        },
        ...options.slice(1),
      ];
    }
    return options;
  }, [displayDocuments, initialDocumentFilter, initialDocumentFilterLabel, t]);
  const primaryDocumentFilterOptions = useMemo(
    () =>
      documentFilterOptions.filter(
        (option) =>
          option.id === "all" || option.id === DOCUMENT_ACTION_NEEDED_FILTER_ID,
      ),
    [documentFilterOptions],
  );
  const contextualDocumentFilterOptions = useMemo(
    () =>
      documentFilterOptions.filter(
        (option) =>
          option.id !== "all" && option.id !== DOCUMENT_ACTION_NEEDED_FILTER_ID,
      ),
    [documentFilterOptions],
  );

  useEffect(() => {
    setActiveDocumentFilter(initialDocumentFilter ?? "all");
  }, [initialDocumentFilter]);

  useEffect(() => {
    setActiveCategoryFilters(
      initialCategoryFilter ? [initialCategoryFilter] : [],
    );
  }, [initialCategoryFilter]);

  const defaultUploadTags = useMemo(
    () =>
      activeDocumentFilter.startsWith(DOCUMENT_CYCLE_TAG_PREFIX)
        ? [activeDocumentFilter]
        : [],
    [activeDocumentFilter],
  );
  const uploadSlotCategory = useMemo(
    () => filteredSlots.find((slot) => slot.id === uploadSlot?.id)?.category,
    [filteredSlots, uploadSlot?.id],
  );
  const selectedUploadCategory = useMemo<DocumentCategory | null>(() => {
    if (activeCategoryFilters.length !== 1) return null;
    const [category] = activeCategoryFilters;
    if (!category || category === "uncategorized") return null;
    return category;
  }, [activeCategoryFilters]);
  const uploadDefaultCategory = uploadSlotCategory ?? selectedUploadCategory;
  const uploadDefaultTags = useMemo(() => {
    if (!uploadDefaultCategory) return defaultUploadTags;
    return Array.from(new Set([...defaultUploadTags, uploadDefaultCategory]));
  }, [defaultUploadTags, uploadDefaultCategory]);

  useEffect(() => {
    if (
      !documentFilterOptions.some(
        (option) => option.id === activeDocumentFilter,
      )
    ) {
      setActiveDocumentFilter("all");
    }
  }, [activeDocumentFilter, documentFilterOptions]);

  const statuses = useMemo(
    () =>
      deriveStatuses(
        displayDocuments,
        filteredSlots.map((s) => s.id),
      ),
    [displayDocuments, filteredSlots],
  );

  const slotClinicalValues = useMemo(
    () => buildSlotTestResults(displayDocuments, displayTestResults),
    [displayDocuments, displayTestResults],
  );

  const slotDates = useMemo(
    () => buildSlotDates(displayDocuments),
    [displayDocuments],
  );

  const slotMedications = useMemo(
    () => buildSlotMedications(displayDocuments),
    [displayDocuments],
  );

  const slotAppointments = useMemo(
    () => buildSlotAppointments(displayDocuments),
    [displayDocuments],
  );

  const flatDocumentItems = useMemo<FlatDocumentItem[]>(() => {
    const items: FlatDocumentItem[] = [];

    for (const slot of filteredSlots) {
      const status = statuses[slot.id] ?? "not_started";
      if (status === "not_started") continue;
      const matchingDoc = displayDocuments?.find((doc) =>
        doc.tags.includes(slot.id),
      );
      const title = t(slot.nameKey);
      const subtitle = t(slot.whyKey);
      items.push({
        kind: "slot",
        id: slot.id,
        category: slot.category,
        slot,
        status,
        document: matchingDoc,
        date: slotDates[slot.id] ?? null,
        actionNeeded:
          status === "requested" ||
          (matchingDoc ? isDocumentRequested(matchingDoc) : false),
        searchText: `${title} ${subtitle}`.toLowerCase(),
        sortLabel: title,
      });
    }

    for (const doc of displayDocuments ?? []) {
      if (doc.tags.some((tag) => allSlotIds.has(tag))) continue;
      const category = getDocumentCategoryFromTags(doc.tags);
      const categoryMeta = getCategoryMeta(category);
      const categoryDescription = t(categoryMeta.descriptionKey);
      items.push({
        kind: "free",
        id: doc.id,
        category,
        document: doc,
        date: doc.documentDate ?? doc.createdAt ?? null,
        actionNeeded: isDocumentRequested(doc),
        searchText: `${doc.name} ${categoryDescription}`.toLowerCase(),
        sortLabel: doc.name,
      });
    }

    return items.sort(compareFlatDocumentItems);
  }, [allSlotIds, displayDocuments, filteredSlots, slotDates, statuses, t]);

  const hasUncategorizedDocuments = flatDocumentItems.some(
    (item) => item.category === "uncategorized",
  );

  const categoryFilterOptions = useMemo<CategoryFilterMeta[]>(() => {
    const options: CategoryFilterMeta[] = [...CATEGORIES];
    if (
      hasUncategorizedDocuments ||
      activeCategoryFilters.includes("uncategorized")
    ) {
      options.push(UNCATEGORIZED_CATEGORY);
    }
    return options;
  }, [activeCategoryFilters, hasUncategorizedDocuments]);

  const visibleDocumentItems = useMemo(
    () =>
      flatDocumentItems.filter((item) => {
        if (!flatDocumentItemMatchesFilter(item, activeDocumentFilter)) {
          return false;
        }
        if (
          activeCategoryFilters.length > 0 &&
          !activeCategoryFilters.includes(item.category)
        ) {
          return false;
        }
        if (searchQuery.trim()) {
          return item.searchText.includes(searchQuery.trim().toLowerCase());
        }
        return true;
      }),
    [
      activeCategoryFilters,
      activeDocumentFilter,
      flatDocumentItems,
      searchQuery,
    ],
  );

  const handleSlotPress = (id: string) => {
    const slot = filteredSlots.find((s) => s.id === id);
    if (!slot) return;

    const status = statuses[id] ?? "not_started";

    if (status === "have_it" && displayDocuments) {
      const matchingDoc = displayDocuments.find((doc) => doc.tags.includes(id));
      if (matchingDoc) {
        setPreviewDocumentInfo({
          slotId: slot.id,
          slotName: t(slot.nameKey),
          slotWhy: t(slot.whyKey),
          document: matchingDoc,
          clinicalValues: slotClinicalValues[slot.id],
        });
        setPreviewModalVisible(true);
        return;
      }
    }

    setUploadSlot({
      id: slot.id,
      name: t(slot.nameKey),
      why: t(slot.whyKey),
    });
    setPendingUploadFiles([]);
    setUploadModalVisible(true);
  };

  const handleFreeUpload = () => {
    setPendingUploadFiles([]);
    setUploadSlot(null);
    setUploadModalVisible(true);
  };

  const handleDroppedFreeUpload = (files: SelectedFile[]) => {
    setPendingUploadFiles(files);
    setUploadSlot(null);
    setUploadModalVisible(true);
  };

  const handleDocumentFilterPress = (filterId: string) => {
    if (filterId === "all") {
      setActiveDocumentFilter("all");
      setActiveCategoryFilters([]);
      return;
    }
    setActiveDocumentFilter(filterId);
  };

  const handleCategoryFilterPress = (category: DocumentFilterCategory) => {
    setActiveCategoryFilters((prev) =>
      prev.includes(category)
        ? prev.filter((item) => item !== category)
        : [...prev, category],
    );
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setActiveDocumentFilter("all");
    setActiveCategoryFilters([]);
  };

  const handleUploadClose = () => {
    setUploadModalVisible(false);
    setUploadSlot(null);
    setPendingUploadFiles([]);
  };

  const handleReuploadFromPreview = () => {
    setPreviewModalVisible(false);
    if (previewDocumentInfo) {
      setUploadSlot({
        id: previewDocumentInfo.slotId,
        name: previewDocumentInfo.slotName,
        why: previewDocumentInfo.slotWhy,
      });
      setPendingUploadFiles([]);
      setUploadModalVisible(true);
    }
  };

  const renderDocumentItem = (item: FlatDocumentItem) => {
    const categoryMeta = getCategoryMeta(item.category);

    if (item.kind === "slot") {
      const primaryClinicalValue =
        slotClinicalValues[item.slot.id]?.[0] ?? null;
      if (item.category === "medical_records" && primaryClinicalValue) {
        const insight = clinicalValueToInsight(
          item.slot,
          primaryClinicalValue,
          t,
        );
        return (
          <DocumentTestResultRow
            key={item.id}
            title={insight.testName}
            subtitle={insight.testPurpose}
            dateLabel={formatDocumentRowDate(item.date, t, locale)}
            value={insight.value}
            unit={insight.unit}
            category={insight.category}
            onPress={() => handleSlotPress(item.slot.id)}
          />
        );
      }

      if (shouldUseDocumentRow(item.category)) {
        return (
          <DocumentRow
            key={item.id}
            title={t(item.slot.nameKey)}
            subtitle={t(item.slot.whyKey)}
            dateLabel={formatDocumentRowDate(item.date, t, locale)}
            imageSource={categoryMeta.image}
            status={getDocumentRowStatus(item.status)}
            onPress={() => handleSlotPress(item.slot.id)}
          />
        );
      }

      const isReadOnly = item.document?.readOnly === true;
      return (
        <DocumentCard
          key={item.id}
          slot={item.slot}
          status={item.status}
          recordDate={item.date}
          clinicalValues={slotClinicalValues[item.slot.id]}
          medications={slotMedications[item.slot.id]}
          appointments={slotAppointments[item.slot.id]}
          overrideType={
            item.category === "medical_records"
              ? getSlotDocType(item.slot.id)
              : undefined
          }
          onChangeType={
            item.category === "medical_records" && !isReadOnly
              ? (type) => handleChangeType(item.slot.id, type)
              : undefined
          }
          onPress={() => handleSlotPress(item.slot.id)}
        />
      );
    }

    const openDocument = () => {
      setPreviewDocumentInfo({
        slotId: item.document.id,
        slotName: item.document.name,
        slotWhy: "",
        document: item.document,
      });
      setPreviewModalVisible(true);
    };

    if (shouldUseDocumentRow(item.category)) {
      return (
        <DocumentRow
          key={item.id}
          title={item.document.name}
          subtitle={t(categoryMeta.descriptionKey)}
          dateLabel={formatDocumentRowDate(item.date, t, locale)}
          imageSource={categoryMeta.image}
          icon={
            categoryMeta.image ? undefined : (
              <FileText size={17} color={EMBER.colors.warm} strokeWidth={1.6} />
            )
          }
          status={item.actionNeeded ? "requested" : "on-file"}
          onPress={openDocument}
        />
      );
    }

    return (
      <Pressable
        key={item.id}
        onPress={() => {
          capturePress(pressLabel(item.document.name), "open_document");
          openDocument();
        }}
        style={{
          ...GLASS_CARD_STYLE,
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          padding: 14,
        }}
      >
        <View
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            backgroundColor: `${EMBER.colors.warm}10`,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FileText size={16} color={EMBER.colors.warm} strokeWidth={1.5} />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              fontSize: 13,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {item.document.name}
          </Text>
          {item.date ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: EMBER.colors.textFaint,
                marginTop: 2,
              }}
            >
              {formatDocumentRowDate(item.date, t, locale)}
            </Text>
          ) : null}
        </View>
        <StatusTag
          status={item.actionNeeded ? "requested" : "have_it"}
          slotId={
            item.category === "medical_records" ? item.document.id : undefined
          }
          overrideType={
            item.category === "medical_records"
              ? (optimisticTypes[item.document.id] ??
                getDocTypeFromTags(item.document.tags))
              : undefined
          }
          onChangeType={
            item.category === "medical_records" && !item.document.readOnly
              ? (type) => handleChangeFreeDocType(item.document.id, type)
              : undefined
          }
        />
      </Pressable>
    );
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View className="flex-row items-start justify-between">
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(
                  t(
                    route.params?.returnTab
                      ? "you.shared.back"
                      : "documents.back",
                  ),
                ),
                "documents.back",
              );
              handleBack();
            }}
            className="mb-2 flex-row items-center gap-1"
          >
            <ChevronLeft size={18} color={EMBER.colors.warm} />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, color: EMBER.colors.warm }}
            >
              {route.params?.returnTab
                ? t("you.shared.back")
                : t("documents.back")}
            </Text>
          </Pressable>
        </View>

        <View style={{ marginBottom: 24 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            }}
          >
            {t("documents.title")}
          </Text>
        </View>
        {documentsDataState === "unavailable" ? (
          <ClinicalDataStatus
            state="unavailable"
            title={t("clinical_data.unavailable_title")}
            message={t("clinical_data.unavailable_body")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={retryDocuments}
            isRetrying={isRetryingDocuments}
            testID="documents-unavailable"
          />
        ) : (
          <>
            {documentsDataState === "stale" ? (
              <ClinicalDataStatus
                state="stale"
                message={t("clinical_data.stale_body")}
                retryLabel={t("clinical_data.retry")}
                retryingLabel={t("clinical_data.retrying")}
                onRetry={retryDocuments}
                isRetrying={isRetryingDocuments}
                style={{ marginBottom: 12 }}
                testID="documents-stale"
              />
            ) : null}

            {/* Search bar + filter */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <MoonSearchInput
                placeholder={t("documents.search_placeholder")}
                value={searchQuery}
                onChangeText={setSearchQuery}
                onClear={() => setSearchQuery("")}
                tone="glass"
                style={{ flex: 1, borderRadius: 12 }}
              />
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel("upload_document"),
                    "upload_document",
                  );
                  handleFreeUpload();
                }}
                style={{
                  ...(GLASS_CARD_STYLE as object),
                  width: 50,
                  height: 44,
                  borderRadius: 12,
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }}
              >
                <Animated.Image
                  source={require("@src/apps/moon/assets/images/care-hero-glow-gradient.png")}
                  style={{
                    position: "absolute",
                    left: -7,
                    top: -8,
                    width: 64,
                    height: 60,
                    opacity: 1,
                    transform: [
                      {
                        translateX: uploadButtonDrift.interpolate({
                          inputRange: [0, 1],
                          outputRange: [-5, 6],
                        }),
                      },
                      {
                        translateY: uploadButtonDrift.interpolate({
                          inputRange: [0, 1],
                          outputRange: [3, -4],
                        }),
                      },
                      {
                        scale: uploadButtonDrift.interpolate({
                          inputRange: [0, 1],
                          outputRange: [1.02, 1.08],
                        }),
                      },
                    ],
                  }}
                  resizeMode="cover"
                />
                <View style={{ zIndex: 2, elevation: 2 }}>
                  <Plus size={19} color="#fff" strokeWidth={2.25} />
                </View>
              </Pressable>
            </View>

            {!displayDocumentsLoading && flatDocumentItems.length > 0 ? (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={{ marginHorizontal: -16, marginBottom: 18 }}
                contentContainerStyle={{
                  gap: 10,
                  paddingLeft: 16,
                  paddingRight: 24,
                }}
              >
                {primaryDocumentFilterOptions.map((filter) => {
                  const selected =
                    filter.id === "all"
                      ? activeDocumentFilter === "all" &&
                        activeCategoryFilters.length === 0
                      : activeDocumentFilter === filter.id;
                  return (
                    <MoonFilterChip
                      key={filter.id}
                      label={filter.label}
                      icon={filter.icon}
                      selected={selected}
                      onPress={() => handleDocumentFilterPress(filter.id)}
                      tone="glass"
                      showCheck={false}
                    />
                  );
                })}
                {categoryFilterOptions.map((category) => (
                  <MoonFilterChip
                    key={category.key}
                    label={t(category.labelKey)}
                    icon={category.icon}
                    selected={activeCategoryFilters.includes(category.key)}
                    onPress={() => handleCategoryFilterPress(category.key)}
                    tone="glass"
                  />
                ))}
                {contextualDocumentFilterOptions.map((filter) => (
                  <MoonFilterChip
                    key={filter.id}
                    label={filter.label}
                    icon={filter.icon}
                    selected={activeDocumentFilter === filter.id}
                    onPress={() => handleDocumentFilterPress(filter.id)}
                    tone="glass"
                    showCheck={false}
                  />
                ))}
              </ScrollView>
            ) : null}

            {displayDocumentsLoading ? (
              <View>
                {Array.from({ length: 4 }).map((_, i) => (
                  <ListRowSkeleton
                    key={i}
                    showLeadingSquare
                    showSubtitle
                    showTrailing
                  />
                ))}
              </View>
            ) : null}
            {!displayDocumentsLoading && flatDocumentItems.length === 0 ? (
              <DocumentsEmptyState
                variant="empty"
                onUpload={handleFreeUpload}
                onFilesSelected={handleDroppedFreeUpload}
                onClearFilters={handleClearFilters}
              />
            ) : null}
            {!displayDocumentsLoading &&
            flatDocumentItems.length > 0 &&
            visibleDocumentItems.length === 0 ? (
              <DocumentsEmptyState
                variant="filtered"
                onUpload={handleFreeUpload}
                onFilesSelected={handleDroppedFreeUpload}
                onClearFilters={handleClearFilters}
              />
            ) : null}
            {!displayDocumentsLoading && visibleDocumentItems.length > 0 ? (
              <View style={{ gap: 10 }}>
                {visibleDocumentItems.map(renderDocumentItem)}
              </View>
            ) : null}
          </>
        )}
      </ScrollView>

      {documentUploadPrototypeState ? (
        <DocumentUploadInteractionPrototype
          state={documentUploadPrototypeState}
          visible
          onClose={() => navigation.goBack()}
        />
      ) : (
        <DocumentUploadModal
          visible={uploadModalVisible}
          onClose={handleUploadClose}
          slot={uploadSlot}
          defaultCategory={uploadDefaultCategory}
          defaultTags={uploadDefaultTags}
          initialFiles={pendingUploadFiles}
          autoPickOnOpen={!visualEvidenceRoute}
        />
      )}

      <DocumentPreviewModal
        visible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        documentInfo={previewDocumentInfo}
        onReupload={handleReuploadFromPreview}
      />
    </GlassScreenLayout>
  );
}
